// src/modules/units/tyres/views/register/tyreregister.js
//
// Tyre Register — the backbone list. Server-side search (by tyre ID), status
// filter and pagination via GET /api/tyre. It is also the engine behind
// Stock/Depot and the inspect/repair/retread action pickers: the controller
// mounts this same view with a preset status or an action, so there is one list,
// not several. (Its built-in search box also covers ID lookup, which is why there
// is no separate Find tile.)
//
// Design notes:
//  - Columns come from the shared registry (shared/tyre-columns.js), which the
//    CSV export also derives from — one definition, two consumers. A mode that
//    cannot populate fitment passes presets.hasFitment:false and the registry
//    drops those columns itself (no per-preset hide list).
//  - Query state lives in the caller-owned `ctx.state` object, so filters and the
//    current page survive a round-trip into a tyre's detail and back.
//  - Loads are guarded: each request bumps a sequence and aborts the previous
//    in-flight fetch, so fast typing can't let a stale response overwrite a newer
//    one (the classic search race).
//  - The toolbar is built once and never re-rendered; only the tbody, the summary
//    and the pager update, so the search box never loses focus mid-type.

import { fetchTyres, fetchAllTyres } from '../tyres.api.js';
// The Unit filter's options come from the customer's vehicle list (own + child
// customers), NOT from a sweep of the tyres — so it lists every vehicle, works at
// All-yards, and does not depend on which trucks happen to have fitted tyres.
import { fetchUnits } from '../units.api.js';
import { unitOptionsFromUnits } from '../../shared/tyre-vehicle-filter.js';
// STATUS_FILTER_OPTIONS / matchesStatusFilter: the dropdown filters on the DERIVED
// status (effectiveStatus, wrapped by matchesStatusFilter) on the client-sweep path,
// not via ?tyreStatus=. So it lists every status we can SHOW, including FITTED /
// IN_SERVICE — derived-only values a server status query could never match — plus the
// two In-Stock refinements (New / Retreaded) that split the shelf by casing life. (See
// load(): selecting any status routes through loadFromSweep.)
import { statusBin, STATUS_FILTER_OPTIONS, matchesStatusFilter } from '../../shared/tyre-status.js';
import { icon } from '../../shared/tyre-icons.js';
import { onTyreEvent, TYRE_EVENTS } from '../../shared/tyre-events.js';
import { getSelectedYard, getSelectedYardId } from '../../shared/tyre-scope.js';
import { esc, formatCount } from '../../shared/tyre-format.js';
// Columns come from the shared registry now — the table and the CSV export derive
// from one definition. resolveColumns() validates a key list on read (drops
// unknown / duplicate / unavailable, re-inserts locked); columnHtml() renders a
// cell (its own render(), else the formatted value, else the muted dash).
import {
  resolveColumns,
  DEFAULT_COLUMN_KEYS,
  columnHtml,
  columnCsv,
  columnHeader
} from '../../shared/tyre-columns.js';
import { loadColumnKeys, saveColumnKeys } from '../../shared/tyre-column-prefs.js';
import { mountColumnPicker } from './columnpicker.js';
import { sortTyres, defaultSortDir } from '../../shared/tyre-sort.js';
import {
  csvCell,
  downloadCsv
} from '../../shared/tyre-csv.js';

// Pagination contract switch. The API returns { count, results }. If `count` is
// the TOTAL rows matching the filter (the usual contract), keep this true: the
// footer shows "x–y of N" and Next disables exactly on the last page. If it turns
// out to be the size of the returned page, set false — paging then relies on a
// full-page heuristic and the total is hidden. One line, one place to flip once
// the backend confirms.
const COUNT_IS_TOTAL = true;

const PAGE_SIZES = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

// The Unit filter's empty (no-filter) option. It reads as a prompt rather than
// "All units" because the list is scoped to the selected yard's customer — "all"
// would be ambiguous (all in scope, or all in the hierarchy?), where "Select a
// Unit" is unambiguous at every scope.
const UNIT_PLACEHOLDER_LABEL = 'Select a Unit';

// Both helpers take the active column set (from the registry) rather than any
// module-level list, so a mode that drops columns gets a matching skeleton and a
// correct colspan.
function skeletonRows(columns, n) {
  const cells = columns.map((c) => `<td class="${c.cls || ''}"><span class="tm-skel"></span></td>`).join('');
  return Array.from({ length: n }, () => `<tr class="tm-row tm-row-skel">${cells}</tr>`).join('');
}

function stateRow(columns, message, actionHtml = '') {
  return `
    <tr class="tm-state-tr"><td class="tm-state-td" colspan="${columns.length}">
      <div class="tm-state">
        <span class="tm-state-icon">${icon('find')}</span>
        <span class="tm-state-msg">${esc(message)}</span>
        ${actionHtml}
      </div>
    </td></tr>`;
}

// The Unit filter's options (gateway IMEI → unitName) now live with the rest of the
// vehicle-filter vocabulary, shared with the fleet report. Re-exported so existing
// importers keep working.
export { unitOptionsFromUnits };

/**
 * Mount the register.
 * @param {HTMLElement} viewEl
 * @param {object} ctx
 * @param {object} ctx.state    caller-owned bag; persists across a detail round-trip
 * @param {object} [ctx.presets] { status, bin, search, focusSearch, banner, hasFitment }
 * @param {(tyre:object)=>void} [ctx.onOpen] called with the row's tyre on open
 * @returns {() => void} teardown
 */
export function renderTyreRegister(viewEl, ctx = {}) {
  const state = ctx.state || {};
  const presets = ctx.presets || {};

  // Availability context for this mode (Stock/Depot and the In-Stock bin pass
  // hasFitment:false; the registry drops the fitment columns via each column's
  // own availability rule).
  const columnCtx = { hasFitment: presets.hasFitment };

  // The picker is offered only where every column is available. That keeps its
  // Apply from silently stripping a column that is merely hidden in this mode, and
  // it is why the stored choice can be read the same way everywhere: in a
  // restricted mode resolveColumns() just drops what can't be populated, so the
  // view shows the available subset of the user's chosen set.
  const canPickColumns = presets.hasFitment !== false;

  // The Unit filter appears only where filtering by a fitted vehicle is
  // meaningful: not in a bin (the bin already fixes what is listed) and not in a
  // no-fitment mode like Stock/Depot (in-stock tyres have no vehicle, so a unit
  // pick could only ever empty the list). It is INDEPENDENT of the yard scope —
  // vehicles belong to the customer, not to a yard — so it stays available at
  // All-yards, which is the whole point of this change.
  const showUnitFilter = !presets.bin && presets.hasFitment !== false;

  // Active columns: the user's stored choice (per user) or the default. The keys
  // are validated on read by resolveColumns (unknown/duplicate/unavailable dropped,
  // locked re-inserted), so a stale stored list degrades gracefully. Mutable
  // because Apply in the picker swaps the set live, with no refetch.
  let columns = resolveColumns(loadColumnKeys() || DEFAULT_COLUMN_KEYS, columnCtx);

  // Seed persisted state on first mount only (so a detail round-trip restores it).
  if (state.__init !== true) {
    state.search = presets.search || '';
    state.status = presets.status || '';
    state.unit = presets.unit || '';
    state.pageSize = DEFAULT_PAGE_SIZE;
    state.offset = 0;
    state.sort = null;           // { key, dir } | null — survives a detail round-trip
    state.__init = true;
  }
  state.rows = state.rows || [];

  let seq = 0;              // request generation guard
  let ctrl = null;         // AbortController for the in-flight request
  let debounceId = null;   // search debounce timer

  const statusOptions = ['<option value="">All statuses</option>']
    .concat(STATUS_FILTER_OPTIONS.map((o) =>
      `<option value="${esc(o.value)}"${o.value === state.status ? ' selected' : ''}>${esc(o.label)}</option>`))
    .join('');

  const pageSizeOptions = PAGE_SIZES.map((n) =>
    `<option value="${n}"${n === state.pageSize ? ' selected' : ''}>${n} / page</option>`).join('');

  const tpl = document.createElement('template');
  tpl.innerHTML = `
    <div class="tm-register">
      ${presets.banner ? `<div class="tm-banner">${esc(presets.banner)}</div>` : ''}
      <div class="tm-toolbar">
        <div class="tm-search">
          <span class="tm-search-icon">${icon('find')}</span>
          <input type="search" class="tm-search-input" data-search
                 placeholder="Search by tyre ID" autocomplete="off"
                 value="${esc(state.search)}" />
        </div>
        ${showUnitFilter ? `<select class="tm-select tm-select-unit" data-unit aria-label="Filter by unit" disabled><option value="">${UNIT_PLACEHOLDER_LABEL}</option></select>` : ''}
        ${presets.bin ? '' : `<select class="tm-select" data-status aria-label="Filter by status">${statusOptions}</select>`}
<div class="tm-toolbar-trailing">
  ${canPickColumns ? `<button type="button" class="tm-btn-secondary tm-colpick-toggle" data-colpick-toggle>Columns</button>` : ''}

  <button
    type="button"
    class="tm-btn-secondary"
    data-export
  >
    Export CSV
  </button>

  <select
    class="tm-select"
    data-pagesize
    aria-label="Rows per page"
  >
    ${pageSizeOptions}
  </select>
</div>
      </div>
      ${canPickColumns ? `<div class="tm-colpick" data-colpick hidden></div>` : ''}

      <div class="tm-table-wrap">
        <table class="tm-table">
          <thead data-head></thead>
          <tbody data-body></tbody>
        </table>
      </div>

      <div class="tm-footer">
        <span class="tm-summary" data-summary></span>
        <div class="tm-pager">
          <button type="button" class="tm-page-btn" data-prev>&lsaquo; Prev</button>
          <button type="button" class="tm-page-btn" data-next>Next &rsaquo;</button>
        </div>
      </div>
    </div>`;
  const root = tpl.content.firstElementChild;

  const body = root.querySelector('[data-body]');
  const headEl = root.querySelector('[data-head]');
  const summaryEl = root.querySelector('[data-summary]');
  const prevBtn = root.querySelector('[data-prev]');
  const nextBtn = root.querySelector('[data-next]');
  const searchInput = root.querySelector('[data-search]');
  const statusSel = root.querySelector('[data-status]');
  const unitSel = root.querySelector('[data-unit]');
  const exportBtn = root.querySelector('[data-export]');
  const pageSel = root.querySelector('[data-pagesize]');


  function renderHead() {
    headEl.innerHTML = `<tr>${columns.map((c) => {
      const sorted = state.sort && state.sort.key === c.key;
      const arrow = sorted ? (state.sort.dir === 'asc' ? '\u2191' : '\u2193') : '';
      return `<th class="${c.cls || ''}${sorted ? ' is-sorted' : ''}">` +
        `<button type="button" class="tm-th-sort" data-sort-key="${esc(c.key)}" title="Sort by ${esc(c.header)}">` +
        `<span class="tm-th-label">${esc(c.header)}</span>` +
        `<span class="tm-th-arrow" aria-hidden="true">${arrow}</span>` +
        `</button></th>`;
    }).join('')}</tr>`;
  }

  function renderBody() {
    if (state.loading && state.rows.length === 0) {
      body.innerHTML = skeletonRows(columns, Math.min(state.pageSize, 8));
    } else if (state.error) {
      body.innerHTML = stateRow(columns, state.error, `<button type="button" class="tm-retry" data-retry>Retry</button>`);
    } else if (state.rows.length === 0) {
      const filtered = !!(state.search || state.status || state.unit);
      body.innerHTML = stateRow(columns, 'No tyres found',
        filtered ? `<button type="button" class="tm-retry" data-clear>Clear filters</button>` : '');
    } else {
      body.innerHTML = state.rows.map((t) =>
        `<tr class="tm-row">${columns.map((c) => `<td class="${c.cls || ''}">${columnHtml(c, t)}</td>`).join('')}</tr>`
      ).join('');
    }
    body.classList.toggle('is-loading', !!state.loading && state.rows.length > 0);

    const from = state.rows.length ? state.offset + 1 : 0;
    const to = state.offset + state.rows.length;
    const hasPrev = state.offset > 0;
    const hasNext = (COUNT_IS_TOTAL && state.count != null)
      ? (state.offset + state.rows.length) < state.count
      : (state.rows.length === state.pageSize && state.rows.length > 0);

    summaryEl.textContent = state.loading ? 'Loading…'
      : state.error ? ''
        : state.rows.length === 0 ? ''
          : (COUNT_IS_TOTAL && state.count != null)
            ? `${formatCount(from)}–${formatCount(to)} of ${formatCount(state.count)}`
            : `Showing ${formatCount(from)}–${formatCount(to)}`;

    prevBtn.disabled = !hasPrev || state.loading;
    nextBtn.disabled = !hasNext || state.loading;
  }

  // The client-sweep cache: the fleet fetched once, reused for every filter, sort
  // and page change until its SCOPE changes or a write lands. Both a derived bin and
  // an active sort run off it. Its scope is the yard's tyres — OR, when a unit is
  // selected, that vehicle's tyres regardless of home yard (see load's note). The
  // key captures that so the sweep refetches when the yard or the unit changes,
  // without needing an explicit reset at each call site.
  let sweepRows = null;
  let sweepKey = null;
  function currentSweepKey() {
    return state.unit ? `unit:${state.unit}` : `yard:${getSelectedYardId() || ''}`;
  }

  async function load() {
    const mySeq = ++seq;
    if (ctrl) ctrl.abort();
    ctrl = new AbortController();

    state.loading = true;
    state.error = null;
    renderBody();

    // A toolbar STATUS filter joins bins and sorts on the client-sweep path. The
    // Status column and the dashboard tile both show the DERIVED status
    // (effectiveStatus), so the filter has to match that — which ?tyreStatus= (a raw
    // stored-column match on the server) cannot express. Without this, a tyre reading
    // "In Stock" off a stale UNDER_REPAIR stamp shows in the column yet is dropped by
    // its own In-Stock filter (the 22-vs-17 split). Sweep + derive keeps the count, the
    // column and the filter in agreement; FITTED / IN_SERVICE — derived-only, never
    // stored — become filterable here too, where a server status query returned nothing.
    if (presets.bin || state.status || state.sort) { await loadFromSweep(mySeq); return; }

    const params = {
      limit: state.pageSize,
      offset: state.offset,
      // A selected UNIT is itself a location scope — the vehicle is a mobile
      // store-house, so its fitted tyres should show wherever each was booked in.
      // When a unit is chosen the home-yard filter is therefore dropped; the fleet
      // still stays within the signed-in customer's hierarchy because GET /api/tyre
      // is scoped by the account. With no unit, the yard filters by booking yard as
      // before.
      yardId: state.unit ? undefined : (getSelectedYardId() || undefined),
      // state.unit holds the vehicle's gateway IMEI. GET /api/tyre filters
      // associated tyres by `imeiNumber` — it has no `unitId` parameter, so the
      // previous `unitId=` here was silently ignored by the server.
      imeiNumber: state.unit || undefined,
      // No tyreStatus here: a status filter is served on the client-sweep path (see
      // load()), matched against the DERIVED status, not the stored column a
      // ?tyreStatus= query compares. This paged path only runs with a blank status.
      search: (state.search || '').trim() || undefined
    };

    let res;
    try {
      res = await fetchTyres(params, { signal: ctrl.signal });
    } catch (e) {
      if (e && e.name === 'AbortError') return;   // superseded by a newer load
      if (mySeq !== seq) return;
      state.loading = false;
      state.error = 'Could not load tyres. Please try again.';
      renderBody();
      return;
    }
    if (mySeq !== seq) return;                     // a newer load already won

    state.loading = false;
    if (!res.success) {
      state.rows = [];
      state.error = res.message || 'Could not load tyres.';
    } else {
      state.rows = Array.isArray(res.data?.results) ? res.data.results : [];
      state.count = res.data?.count ?? null;
    }
    renderBody();
  }

  // Client-sweep path — the fleet comes back once (cached per yard) and the
  // filtering, sorting and paging happen here. Two things need it: a derived BIN
  // (which cannot be expressed as ?tyreStatus=, so the server would disagree with
  // the tile pressed), and an active SORT (correct order needs the whole set, not
  // one page, and derived columns can only be sorted here). With neither in play,
  // load() stays on the efficient server-paged path. Retire the bin half with the
  // dashboard's copy when the backend can answer by bin.
  async function loadFromSweep(mySeq) {
    const key = currentSweepKey();
    if (!sweepRows || sweepKey !== key) {
      let res;
      try {
        // Mirror the paged path's scope: a selected unit sweeps that vehicle's
        // tyres (any home yard); otherwise the selected yard's tyres. Fetching by
        // imeiNumber keeps this narrow rather than sweeping the whole fleet.
        res = await fetchAllTyres(
          state.unit ? { imeiNumber: state.unit } : { yardId: getSelectedYardId() },
          { signal: ctrl.signal }
        );
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        if (mySeq !== seq) return;
        state.loading = false;
        state.error = 'Could not load tyres. Please try again.';
        renderBody();
        return;
      }
      if (mySeq !== seq) return;
      if (!res.success) {
        state.loading = false;
        state.rows = [];
        state.error = res.message || 'Could not load tyres.';
        renderBody();
        return;
      }
      sweepRows = res.data?.results || [];
      sweepKey = key;
    }

    // The same filters the server would apply, applied here instead. A bin is a
    // derived bucket; status is the toolbar filter, matched against the DERIVED
    // status (effectiveStatus) so it agrees with the Status column and the tile —
    // blank in bin mode, where its control is hidden; search is by tyre ID. The unit
    // clause is defensive only — when a unit is selected the sweep was already fetched
    // scoped to that vehicle's imeiNumber above, so every row already matches.
    const q = (state.search || '').trim().toLowerCase();
    let rows = sweepRows.filter((t) =>
      (!presets.bin || statusBin(t) === presets.bin) &&
      matchesStatusFilter(t, state.status) &&
      (!state.unit || String(t.imeiNumber ?? '') === state.unit) &&
      (!q || String(t.externalTyreId ?? '').toLowerCase().includes(q))
    );

    if (state.sort) rows = sortTyres(rows, state.sort.key, state.sort.dir);

    state.loading = false;
    state.error = null;
    state.count = rows.length;
    state.rows = rows.slice(state.offset, state.offset + state.pageSize);
    renderBody();
  }

  async function exportCurrentView() {
    if (!exportBtn || exportBtn.disabled) return;

    exportBtn.disabled = true;
    const oldLabel = exportBtn.textContent;
    exportBtn.textContent = 'Exporting…';

    try {
      /*
       * We need ALL matching tyres, not only state.rows.
       *
       * state.rows contains just the current page.
       *
       * If we already have a current sweep cached, reuse it.
       * Otherwise fetch the entire current Yard / Unit scope.
       */
      const key = currentSweepKey();

      let rows;

      if (sweepRows && sweepKey === key) {
        rows = sweepRows.slice();
      } else {
        const res = await fetchAllTyres(
          state.unit
            ? {
              imeiNumber: state.unit
            }
            : {
              yardId: getSelectedYardId() || undefined
            }
        );

        if (!res.success) {
          exportBtn.textContent = 'Export failed';

          setTimeout(() => {
            if (exportBtn) {
              exportBtn.textContent = oldLabel;
              exportBtn.disabled = false;
            }
          }, 1500);

          return;
        }

        rows = res.data?.results || [];
      }

      /*
       * Apply exactly the same filters as the Register.
       */
      const q = (state.search || '')
        .trim()
        .toLowerCase();

      rows = rows.filter((t) =>
        (!presets.bin || statusBin(t) === presets.bin) &&
        matchesStatusFilter(t, state.status) &&
        (!state.unit || String(t.imeiNumber ?? '') === state.unit) &&
        (!q || String(t.externalTyreId ?? '').toLowerCase().includes(q))
      );

      /*
       * Preserve the Register's current sort as well.
       */
      if (state.sort) {
        rows = sortTyres(
          rows,
          state.sort.key,
          state.sort.dir
        );
      }

      if (!rows.length) {
        exportBtn.textContent = 'No rows to export';

        setTimeout(() => {
          if (exportBtn) {
            exportBtn.textContent = oldLabel;
            exportBtn.disabled = false;
          }
        }, 1500);

        return;
      }

      /*
       * IMPORTANT:
       *
       * `columns` is the CURRENT visible/selected column list,
       * including the order chosen in the Columns picker.
       */
      const header = columns
        .map((col) =>
          csvCell(columnHeader(col, 'csv'))
        )
        .join(',');

      const lines = rows.map((tyre) =>
        columns
          .map((col) =>
            csvCell(columnCsv(col, tyre))
          )
          .join(',')
      );

      const csv = [
        header,
        ...lines
      ].join('\r\n');

      const stamp =
        new Date().toISOString().slice(0, 10);

      downloadCsv(
        `tyre-register-${stamp}.csv`,
        csv
      );
    } finally {
      exportBtn.textContent = oldLabel;
      exportBtn.disabled = false;
    }
  }

  // Unit filter options — the customer's vehicles, from units.api.js. The list is
  // fetched per scope (this yard's customer sub-tree, or the whole account at
  // All-yards) and cached per customer in `state`, so re-visiting a yard or a detail
  // round-trip does not re-fetch. The fetch never blocks the tyre list — if it fails
  // the filter simply stays unavailable rather than taking the table down with it.
  let unitLoadSeq = 0;

  function paintUnitOptions(opts, selected) {
    if (!unitSel) return;
    unitSel.innerHTML = [`<option value="">${UNIT_PLACEHOLDER_LABEL}</option>`]
      .concat(opts.map((u) => `<option value="${esc(u.id)}"${u.id === selected ? ' selected' : ''}>${esc(u.label)}</option>`))
      .join('');
    unitSel.value = selected || '';
  }

  // The scope key for the CURRENT yard: the yard's owning customer, or a sentinel
  // for All-yards (which scopes to the signed-in account). Vehicles are cached per
  // scope in state.unitsByCustomer so re-visiting a yard — or a detail round-trip —
  // does not re-fetch.
  const ALL_UNITS_KEY = '__all__';
  function unitScopeKey() {
    return getSelectedYard()?.customerId || ALL_UNITS_KEY;
  }

  // Paint + enable/disable from a resolved options list. `null` means the fetch
  // failed (or has not run) — the control is left explicitly unavailable rather
  // than looking like an empty account. A selection the scope no longer contains is
  // pruned, though the SCOPE_CHANGED handler already clears it on a yard change, so
  // this mainly guards a stale persisted value.
  function applyUnitOptions(opts) {
    if (!unitSel) return;
    if (!Array.isArray(opts)) {
      paintUnitOptions([], '');
      unitSel.disabled = true;
      unitSel.title = 'Units unavailable';
      return;
    }
    if (state.unit && !opts.some((u) => u.id === state.unit)) state.unit = '';
    paintUnitOptions(opts, state.unit || '');
    unitSel.disabled = opts.length === 0;
    if (opts.length === 0) unitSel.title = 'No vehicles in this scope';
    else unitSel.removeAttribute('title');
  }

  // Load the vehicles for the CURRENT scope: at All-yards the whole account; under a
  // specific yard, that yard's customer AND its descendants. The hierarchy walk is
  // the endpoint's job — fetchUnits({ customerId }) re-roots the scope and the
  // server returns that customer plus everyone below it — so there is no client-side
  // customer tree here. Cached per customer; the fetch never blocks the tyre list —
  // if it fails the filter just stays unavailable.
  //
  // Note: only vehicles owned within the yard's customer sub-tree are offered. A
  // tyre homed at this yard but fitted to a vehicle OUTSIDE that sub-tree would not
  // be filterable here; that is unusual given the ownership model, and All-yards
  // always exposes every vehicle.
  async function loadUnitOptions() {
    if (!unitSel) return;                          // hidden in bin / no-fitment modes

    const key = unitScopeKey();
    const cache = state.unitsByCustomer || (state.unitsByCustomer = {});

    // Cache hit for this scope.
    if (Array.isArray(cache[key])) { applyUnitOptions(cache[key]); return; }

    const mySeq = ++unitLoadSeq;
    unitSel.disabled = true;
    unitSel.innerHTML = `<option value="">Loading units…</option>`;

    // undefined customerId → fetchUnits scopes to the signed-in account (All-yards).
    const res = await fetchUnits({ customerId: getSelectedYard()?.customerId || undefined });
    if (mySeq !== unitLoadSeq) return;             // a newer scope/load won the race

    if (!res.success) {
      applyUnitOptions(null);                      // not cached → retried on next scope visit
      return;
    }

    const opts = unitOptionsFromUnits(res.data?.results || []);
    cache[key] = opts;
    applyUnitOptions(opts);
  }

  // ---- events (wired once) ----
  searchInput.addEventListener('input', () => {
    clearTimeout(debounceId);
    debounceId = setTimeout(() => {
      state.search = searchInput.value;
      state.offset = 0;
      load();
    }, SEARCH_DEBOUNCE_MS);
  });

  // Absent in bin mode: the bin already decides what is listed, so a second
  // status filter on top of it could only produce an empty list.
  if (statusSel) {
    statusSel.addEventListener('change', () => {
      state.status = statusSel.value;
      state.offset = 0;
      load();
    });
  }

  if (unitSel) {
    unitSel.addEventListener('change', () => {
      state.unit = unitSel.value;
      state.offset = 0;
      load();
    });
  }

  exportBtn.addEventListener('click', exportCurrentView);

  pageSel.addEventListener('change', () => {
    state.pageSize = Number(pageSel.value) || DEFAULT_PAGE_SIZE;
    state.offset = 0;
    load();
  });

  prevBtn.addEventListener('click', () => {
    if (state.offset <= 0) return;
    state.offset = Math.max(0, state.offset - state.pageSize);
    load();
  });

  nextBtn.addEventListener('click', () => {
    state.offset += state.pageSize;
    load();
  });

  body.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-open]');
    if (openBtn) {
      const id = openBtn.getAttribute('data-open');
      const tyre = state.rows.find((r) => String(r.externalTyreId) === String(id));
      if (tyre && typeof ctx.onOpen === 'function') ctx.onOpen(tyre);
      return;
    }
    if (e.target.closest('[data-retry]')) { load(); return; }
    if (e.target.closest('[data-clear]')) {
      state.search = '';
      state.status = '';
      state.unit = '';
      state.offset = 0;
      searchInput.value = '';
      if (statusSel) statusSel.value = '';
      if (unitSel) unitSel.value = '';
      load();
    }
  });

  renderHead();
  viewEl.replaceChildren(root);
  if (presets.focusSearch) searchInput.focus();
  loadUnitOptions(); // load the vehicles for the current scope (this yard's customer sub-tree, or the whole account)

  // Column picker (only where all columns are available — see canPickColumns).
  // Apply swaps the columns live: persist, re-resolve, repaint head + body. No
  // refetch — columns are a view over the rows already loaded.
  let picker = null;
  if (canPickColumns) {
    picker = mountColumnPicker(root.querySelector('[data-colpick]'), {
      ctx: columnCtx,
      getCurrentKeys: () => columns.map((c) => c.key),
      onApply: (keys) => {
        saveColumnKeys(keys);
        columns = resolveColumns(keys, columnCtx);
        renderHead();
        renderBody();
      }
    });
    root.querySelector('[data-colpick-toggle]').addEventListener('click', () => picker.toggle());
  }

  // Click a header to sort by that column. First click takes the column's natural
  // direction (highest/newest first for numbers/dates, A→Z for text — so one
  // click on Highest temp puts the hottest on top); a second click flips it; a
  // third clears the sort and returns to the server-paged order.
  headEl.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-sort-key]');
    if (!btn) return;
    const key = btn.getAttribute('data-sort-key');
    const cur = state.sort;
    if (!cur || cur.key !== key) {
      state.sort = { key, dir: defaultSortDir(key) };
    } else if (cur.dir === defaultSortDir(key)) {
      state.sort = { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' };
    } else {
      state.sort = null;
    }
    state.offset = 0;
    renderHead();
    load();
  });

  // Refresh when a mutation elsewhere reports a change (the refresh-cycle seam).
  // Drop the sweep cache too, so a write is reflected the next time a bin or a
  // sort re-reads the fleet rather than showing the pre-write snapshot.
  const off = onTyreEvent(TYRE_EVENTS.CHANGED, () => { sweepRows = null; load(); });

  // Re-query when the yard scope changes. The yard is a server-side filter on the
  // tyre list, so the page is re-fetched (not filtered in place), the pager returns
  // to the first page, and the cached fleet sweep — for the previous yard — is
  // dropped so a bin or sort re-sweeps the new one. The unit filter is CLEARED and
  // its options reloaded for the new yard's customer sub-tree: the vehicle list is
  // re-scoped on every yard change, so carrying a selection forward could leave the
  // dropdown showing a value that is not in its own list. Clearing first also means
  // load() below never applies a stale unit filter while the new list is fetching.
  const offScope = onTyreEvent(TYRE_EVENTS.SCOPE_CHANGED, () => {
    state.offset = 0;
    state.unit = '';
    sweepRows = null;
    loadUnitOptions();
    load();
  });

  load();

  return () => {
    off();
    offScope();
    picker?.teardown();
    clearTimeout(debounceId);
    if (ctrl) ctrl.abort();
  };
}
